import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  DatabaseBackup,
  Landmark,
  Loader2,
  Lock,
  Mail,
  Bot,
  Palette,
  Puzzle,
  Receipt,
  Rocket,
  Scale,
  Settings,
  Share2,
  ShieldCheck,
  Shirt,
  Tags,
  UserCog,
  Upload,
  Wallet,
} from 'lucide-react';
import { fetchParametresAdmin, laisserVierge, updateParametreAdmin } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import EncaissementEnLigne from '../components/EncaissementEnLigne';
import { Link } from 'react-router-dom';
import GestionReferentiels from '../components/GestionReferentiels';
import ParametreField, { type ParametreItem } from '../components/ParametreField';
import IdentiteParSiret from '../components/IdentiteParSiret';
import Personnalisation from '../components/Personnalisation';
import ReseauxSociauxAdmin from '../components/ReseauxSociauxAdmin';
import BotTelegram from '../components/BotTelegram';
import LogosTelegram from '../components/LogosTelegram';
import MiseEnProduction from '../components/MiseEnProduction';
import ModulesOptionnels from '../components/ModulesOptionnels';
import RenouvellementAbonnement from '../components/RenouvellementAbonnement';
import ResilierAbonnement from '../components/ResilierAbonnement';
import TaillesVetements from '../components/TaillesVetements';
import VaultAdministration from '../components/VaultAdministration';
import PartenairesInstitutionnels from '../components/PartenairesInstitutionnels';
import ExporterMesDonnees from '../components/ExporterMesDonnees';
import ImporterDesDonnees from '../components/ImporterDesDonnees';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import SectionParametres from '../components/SectionParametres';

export default function AdminParametresPage() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<string | null>(null);
  const perso = usePersonnalisation();

  // La bannière des paramètres manquants mène au champ : elle ouvre la
  // section (par signal incrémental) puis fait défiler jusqu'au champ,
  // curseur posé dedans — le temps que la section se déplie.
  const [signauxOuverture, setSignauxOuverture] = useState<Record<string, number>>({});
  const allerAuChamp = (p: any) => {
    setSignauxOuverture((prev) => ({ ...prev, [p.groupe]: (prev[p.groupe] ?? 0) + 1 }));
    setTimeout(() => {
      const champ = document.getElementById(p.cle);
      champ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (champ as HTMLElement | null)?.focus?.({ preventScroll: true });
    }, 180);
  };

  const isAdmin = user?.roles?.includes('ROLE_ADMIN');
  const isSuperAdmin = user?.roles?.includes('ROLE_SUPER_ADMIN') === true;

  const parametresQuery = useQuery({
    queryKey: ['admin-parametres'],
    queryFn: fetchParametresAdmin,
    enabled: isAdmin,
  });

  const tousParametres: any[] = parametresQuery.data?.groupes
    ? Object.values(parametresQuery.data.groupes).flat()
    : [];

  // Les parametres de devops (messagerie, Stripe, hebergement) sont geres
  // dans l'ecran dedie, reserve au super administrateur : on les retire ici
  // pour ne pas les dupliquer.
  const groupesVisibles = (parametresQuery.data?.groupes ?? {}) as Record<string, any[]>;
  const groupes = Object.fromEntries(
    // « personnalisation » et « reseaux » ont leur propre section, avec
    // sélecteur de couleur et liste de comptes : les reprendre ici en champs
    // texte ferait doublon.
    Object.entries(groupesVisibles)
      .filter(
        ([g]) =>
          !['devops', 'stripe', 'personnalisation', 'reseaux', 'telegram', 'partenaires'].includes(g)
      )
      // MODE_PRODUCTION se change par ses boutons dédiés (mise en production,
      // retour en essai) : l'afficher ici en lecture seule, avec sa
      // description technique, semait le doute sans rien permettre.
      .map(([g, liste]) => [g, (liste as any[]).filter((p) => p.cle !== 'MODE_PRODUCTION')])
  );

  // Sur trim() : une valeur faite d'espaces n'est pas un parametre renseigne,
  // et ferait disparaitre l'avertissement sans rien configurer. Les champs
  // masques n'exposent pas leur valeur : estRenseigne fait foi pour eux.
  const parametresManquants = tousParametres.filter(
    (p: any) =>
      parametresObligatoires.includes(p.cle) &&
      // Un champ declare vide par le bureau ne se reclame plus : toutes les
      // associations n'ont pas de RNA ni de compte bancaire.
      p.laisseVierge !== true &&
      (p.valeurMasquee ? !p.estRenseigne : !(p.valeur ?? '').trim())
  );

  // « Laisser vierge » : le bureau declare qu'il n'a pas cette information.
  // La banniere cesse de la reclamer, et le champ reste vide — on n'invente
  // pas un numero de parution au Journal officiel.
  const laisserViergeMutation = useMutation({
    mutationFn: ({ cle, vierge }: { cle: string; vierge: boolean }) => laisserVierge(cle, vierge),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-parametres'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ cle, valeur }: { cle: string; valeur: string | null }) => updateParametreAdmin(cle, valeur),
    onSuccess: (data: any, vars) => {
      // Le parametre renvoye par le serveur est reporte dans le cache sans
      // attendre le rechargement complet : la mention « a renseigner »
      // correspondante tombe a l'instant meme de l'enregistrement, champ par
      // champ, au lieu de rester le temps d'un aller-retour.
      const parametre = data?.parametre;
      if (parametre) {
        queryClient.setQueryData(['admin-parametres'], (ancien: any) => {
          if (!ancien?.groupes) return ancien;

          const groupes = Object.fromEntries(
            Object.entries(ancien.groupes as Record<string, any[]>).map(([g, liste]) => [
              g,
              liste.map((p) => (p.cle === parametre.cle ? parametre : p)),
            ])
          );

          return { ...ancien, groupes };
        });
      }

      queryClient.invalidateQueries({ queryKey: ['admin-parametres'] });
      setSaved(vars.cle);
      setTimeout(() => setSaved(null), 2000);
    },
  });

  /**
   * Une section faite des paramètres d'un groupe.
   *
   * Ces sections se répartissent entre les trois familles — la messagerie et
   * la boutique relèvent de la configuration, la facturation et les
   * coordonnées bancaires du financier — et il fallait pouvoir les poser une
   * à une plutôt qu'en bloc à la suite.
   */
  const renduGroupe = (groupe: string) => {
    const liste = groupes[groupe] as ParametreItem[] | undefined;
    if (!liste?.length) {
      return null;
    }

    const { label, icone } = groupeInfos[groupe];

    return (
      <SectionParametres
        key={groupe}
        titre={label}
        icone={icone}
        signalOuverture={signauxOuverture[groupe] ?? 0}
      >
        {/* Dix champs à recopier d'un avis de situation, sans se tromper :
            le répertoire des entreprises les connaît tous. */}
        {groupe === 'facturation' && <IdentiteParSiret />}

        {/* Les options de la plateforme ne font pas partie de l'offre de
            base : l'essai les ouvre toutes pour les découvrir, la production
            les remet à la souscription. */}
        {groupe === 'boutique' && (
          <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
            perso?.boutiqueActive
              ? perso?.modeEssai
                ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
                : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
              : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
          }`}>
            {perso?.modeEssai ? (
              <>
                <strong>Option non incluse dans l'offre de base</strong> — elle est ouverte
                pendant l'essai pour la découvrir. Au passage en production, elle se
                désactivera : elle se souscrit alors sur la boutique de la plateforme
                (20 €/mois ou 200 €/an).
              </>
            ) : perso?.boutiqueActive ? (
              <>Option souscrite : la boutique est ouverte au public.</>
            ) : (
              <>
                <strong>Option non incluse</strong> — la boutique est fermée. Souscrivez-la
                sur <a href="https://shop.gest-asso.com" target="_blank" rel="noopener" className="underline font-medium">shop.gest-asso.com</a>{' '}
                (20 €/mois ou 200 €/an) pour l'ouvrir.
              </>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4">
          {liste.map((p) => (
            <ParametreField
              key={p.cle}
              parametre={p}
              onSave={(cle, valeur) => updateMutation.mutate({ cle, valeur })}
              isSaving={updateMutation.isPending && updateMutation.variables?.cle === p.cle}
              isSaved={saved === p.cle}
              // L'échéance calendaire de la clôture se déduit de la date de
              // déclaration : le champ l'affiche sous le choix.
              dateDeclaration={
                tousParametres.find((x: any) => x.cle === 'ASSOCIATION_DATE_DECLARATION')?.valeur ?? null
              }
              obligatoire={parametresObligatoires.includes(p.cle)}
              onLaisserVierge={(cle, vierge) => laisserViergeMutation.mutate({ cle, vierge })}
            />
          ))}
        </div>
      </SectionParametres>
    );
  };

  if (authLoading || parametresQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée au super-administrateur.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Settings className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Paramètres</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Configurez les coordonnées bancaires, les textes légaux et les référentiels.</p>
        </div>
      </div>

      {parametresManquants.length > 0 && (
        <div className="card bg-amber-50 border-amber-200 text-amber-800 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold flex items-center gap-2">
              Paramètres obligatoires à renseigner
              {/* Le compte en pastille : on voit d'un regard combien il en
                  reste, et il descend à mesure qu'on remplit. */}
              <span className="inline-flex items-center justify-center min-w-[1.4rem] h-[1.4rem] px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold tabular-nums">
                {parametresManquants.length}
              </span>
            </p>
            {/* Chaque mention dit sa section et mène au champ : la section
                s'ouvre, la page défile, le curseur est déjà dedans. */}
            <ul className="mt-1 space-y-0.5">
              {parametresManquants.map((p: any) => (
                <li key={p.cle}>
                  <button
                    type="button"
                    onClick={() => allerAuChamp(p)}
                    className="text-left hover:underline"
                  >
                    <span className="font-medium">{p.label}</span>
                    <span className="opacity-75">
                      {' '}— section {groupeInfos[p.groupe]?.label ?? p.groupe} →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {parametresQuery.isError && (
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <p>
            Impossible de charger les paramètres. Vérifiez que vous êtes connecté en tant
            qu'administrateur.
          </p>
        </div>
      )}

      {/*
        Trois familles, deux colonnes.

        Seize sections empilées faisaient une colonne de plusieurs écrans : on
        ouvrait la messagerie, on descendait, et la facturation se retrouvait
        sous l'horizon. Elles se rangent désormais par nature — ce qui décrit
        l'association, ce qui règle son fonctionnement, ce qui touche à
        l'argent — et sur deux colonnes à partir du grand écran.

        Une grille plutôt que des colonnes CSS : une section qui se déplie ne
        doit pas se couper en deux au milieu d'un champ.
      */}
      <div className="space-y-8">
        <Famille
          titre="Information"
          resume="Ce qui décrit votre association et se retrouve sur votre site public."
        >
          <SectionParametres
            titre="Personnalisation"
            description="Couleurs d'accentuation et logo du site."
            icone={Palette}
          >
            <Personnalisation />
          </SectionParametres>

          <SectionParametres
            titre="Réseaux sociaux"
            description="Les comptes du comité, affichés en bas de la page d'accueil."
            icone={Share2}
          >
            <ReseauxSociauxAdmin />
          </SectionParametres>

          <SectionParametres
            titre="Mairie & CC"
            description="Les partenaires institutionnels : leur nom et leurs visuels, en tête de la vitrine."
            icone={Building2}
          >
            <PartenairesInstitutionnels />
          </SectionParametres>

          {renduGroupe('legal')}
          {renduGroupe('general')}

          {/* La sortie de secours, en évidence et non cachée derrière la
              résiliation : vos données vous appartiennent, l'export se
              clique, il ne se demande pas. */}
          <SectionParametres
            titre="Exporter mes données"
            description="Toute la base en CSV, et tous les documents en ZIP. À tout moment, sans rien demander."
            icone={DatabaseBackup}
          >
            <ExporterMesDonnees />
          </SectionParametres>

          {/* L'import du classeur de reprise : réservé à l'exploitant. La
              reprise est gratuite sous réserve d'un tableau bien rempli, et
              c'est un humain qui juge le tableau — pas un bouton chez le
              client, qui transformerait toute reprise ratée en ticket. */}
          {isSuperAdmin && (
            <SectionParametres
              titre="Importer des données"
              description="Le classeur de reprise, analysé puis importé. Réservé à l'exploitant."
              icone={Upload}
            >
              <ImporterDesDonnees />
            </SectionParametres>
          )}
        </Famille>

        <Famille
          titre="Configuration"
          resume="Ce qui règle le fonctionnement : qui fait quoi, ce qui s'envoie, ce qui est ouvert."
        >
          <SectionParametres
            titre="Rôles et statuts"
            description="Les fonctions au bureau et les postes tenus par les bénévoles sur un événement."
            icone={Tags}
          >
            <GestionReferentiels />
          </SectionParametres>

          {/* Les fonctions se règlent désormais à l'écran des droits, avec le
              statut et l'administration du site — les trois se lisaient
              séparément et se contredisaient. Le renvoi vaut mieux qu'un
              second endroit où modifier la même chose. */}
          <SectionParametres
            titre="Qui tient quelle fonction"
            description="Les fonctions, les statuts et l'administration du site se règlent au même endroit."
            icone={UserCog}
          >
            <Link
              to="/admin/droits"
              className="btn-outline inline-flex items-center gap-2 text-sm"
            >
              <ShieldCheck className="w-4 h-4" /> Ouvrir la gestion des droits
            </Link>
          </SectionParametres>

          {renduGroupe('email')}

          <SectionParametres
            titre="Tailles de vêtements"
            description="Les textiles proposés aux bénévoles et leurs tailles : la liste s'enrichit librement."
            icone={Shirt}
          >
            <TaillesVetements />
          </SectionParametres>

          {renduGroupe('boutique')}

          <SectionParametres
            titre="Bot Telegram"
            description="Le compte automatique du comité sur Telegram : son jeton et son nom."
            icone={Bot}
            actions={<LogosTelegram />}
          >
            <BotTelegram
              parametres={(groupesVisibles.telegram ?? []) as ParametreItem[]}
              onSave={(cle, valeur) => updateMutation.mutate({ cle, valeur })}
              enCoursDeSauvegarde={
                updateMutation.isPending ? (updateMutation.variables?.cle ?? null) : null
              }
              cleSauvegardee={saved}
            />
          </SectionParametres>

          <SectionParametres
            titre="Coffre-fort sécurisé"
            description="Les données du Vault ne s'effacent jamais d'office : le vidage se demande ici, mot de passe à l'appui."
            icone={Lock}
          >
            <VaultAdministration />
          </SectionParametres>

          {/* Visible seulement en essai : une fois la production activée, le
              bouton n'a plus d'objet, et le laisser inviterait à recliquer. */}
          {perso?.modeEssai === true && (
            <SectionParametres
              titre="Mise en production"
              description="Efface les données d'essai et ouvre la numérotation réelle. Irréversible."
              icone={Rocket}
            >
              <MiseEnProduction />
            </SectionParametres>
          )}
        </Famille>

        <Famille
          titre="Financier"
          resume="L'argent : ce qui rentre, sur quel compte, et ce que porte une facture."
        >
          {/* Le prestataire d'encaissement appartient à l'association —
              l'argent arrive chez elle — d'où sa place ici plutôt que dans
              l'écran technique de l'éditeur. */}
          <SectionParametres
            titre="Encaissement en ligne"
            description="Stripe encaisse la carte, Apple Pay, Google Pay et le prélèvement SEPA. Le compte est celui de votre association."
            icone={Wallet}
          >
            <EncaissementEnLigne />
          </SectionParametres>

          {renduGroupe('paiement')}
          {renduGroupe('facturation')}

          {/* Ouverte d'office quand on revient de Stripe : le message de
              confirmation (ou d'annulation) doit se voir sans un clic. */}
          <SectionParametres
            titre="Modules"
            description="Les modules optionnels de la plateforme : ce qui est inclus, ce qui se souscrit."
            icone={Puzzle}
            ouverteParDefaut={
              new URLSearchParams(window.location.search).has('modules') ||
              new URLSearchParams(window.location.search).has('renouvellement')
            }
          >
            {/* Le renouvellement passe devant : à trente jours de l'échéance,
                c'est la décision qui presse, pas le catalogue. */}
            <div className="space-y-6">
              <RenouvellementAbonnement />
              <ModulesOptionnels />
              {/* La résiliation en dernier, une fois lu tout ce qui précède :
                  c'est la porte de sortie, pas la première chose qu'on propose. */}
              <ResilierAbonnement />
            </div>
          </SectionParametres>
        </Famille>
      </div>
    </div>
  );
}

/**
 * Une famille de réglages : son titre, et ses sections sur deux colonnes.
 *
 * Le titre n'est pas décoratif — il dit dans quelle des trois natures on
 * cherche, et c'est ce qui remplace le défilement.
 */
function Famille({
  titre,
  resume,
  children,
}: {
  titre: string;
  resume: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <header className="px-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {titre}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{resume}</p>
      </header>
      {/* Les cartes s'étirent à la hauteur de leur rangée, et leur contenu
          s'aligne à l'intérieur : titre en haut, résumé au milieu, actions en
          bas. Avec « items-start », chacune tombait à sa propre hauteur et la
          grille présentait des bas de cartes en escalier — un titre de deux
          lignes suffisait à décaler toute la colonne.

          Le repli ne se joue plus ici de toute façon : une section s'ouvre
          dans une fenêtre, la carte ne se déplie pas sur place. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}



const groupeInfos: Record<string, { label: string; icone: any }> = {
  email: { label: 'Messagerie', icone: Mail },
  facturation: { label: 'Facturation', icone: Receipt },
  legal: { label: 'Mentions légales', icone: Scale },
  paiement: { label: 'Coordonnées bancaires', icone: Landmark },
  boutique: { label: 'Boutique (option)', icone: Settings },
  general: { label: 'Général', icone: Settings },
};

const parametresObligatoires = [
  'PAIEMENT_IBAN',
  'PAIEMENT_BIC',
  'PAIEMENT_TITULAIRE',
  'PAIEMENT_ADRESSE_CHEQUE',
  'EMAIL_BUREAU',
  // Les taux de TVA se declarent, meme a zero : une facture sans mention de
  // TVA assumee n'est pas une facture.
  'TVA_SPONSOR',
  'TVA_BOUTIQUE',
  // L'identité de l'association : elle naît vierge sur un espace neuf, et
  // c'est au bureau de la renseigner — personne d'autre ne connaît la date de
  // déclaration en préfecture ni le numéro RNA. Un site public sans ses
  // mentions légales n'est pas en règle.
  'ASSOCIATION_SOUS_TITRE',
  'FACTURATION_RAISON_SOCIALE',
  'FACTURATION_ADRESSE',
  'FACTURATION_CODE_POSTAL',
  'FACTURATION_VILLE',
  'FACTURATION_RNA',
  'ASSOCIATION_OBJET',
  'ASSOCIATION_DATE_DECLARATION',
  'ASSOCIATION_LIEU_DECLARATION',
  'ASSOCIATION_DEPARTEMENT',
  'ASSOCIATION_JO_PARUTION',
  'ASSOCIATION_JO_ANNONCE',
  // MENTIONS_LEGALES_DIRECTEUR_PUBLICATION non plus : pour une association,
  // c'est le president qui repond de ce que le site publie, et le logiciel le
  // connait deja. Le champ reste modifiable pour les cas ou ce n'est pas lui.
  // ASSOCIATION_REPRESENTANT ne figure plus ici, et c'est voulu : le
  // représentant légal est le président, que le logiciel connaît déjà par le
  // bureau. Le réclamer revenait à faire saisir une deuxième fois une
  // information déjà là — et un champ qu'on saisit deux fois finit par porter
  // deux valeurs différentes. Il reste modifiable pour les cas où le
  // représentant n'est pas le président : co-présidence, mandataire désigné
  // en assemblée.
  'RGPD_DPO_EMAIL',
];
